import { Box, Button, FormControl, styled, Stack, Checkbox, Divider, FormControlLabel, FormLabel, Link, TextField, Typography, Avatar } from "@mui/material";
import { GoogleIcon } from './CustomIcons';
import MuiCard from '@mui/material/Card';
import React, { useEffect, useState } from "react";
import { doCreateUserWithEmailAndPassword, doSendEmailVerification, doSignInWithGoogle, doSignInWithOrcid, doSignOut } from "../firebase/auth";
import { useAuth } from "../contexts/authContext";
import { Navigate, useNavigate } from "react-router-dom";
import { auth } from "../firebase/firebase";
import { updateProfile } from "firebase/auth";

const Card = styled(MuiCard)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: 'auto',
    boxShadow:
        'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
    [theme.breakpoints.up('sm')]: {
        width: '450px',
    },
    ...theme.applyStyles('dark', {
        boxShadow:
            'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
    }),
}));

const SignUpContainer = styled(Stack)(({ theme }) => ({
    minHeight: '100%',
    padding: theme.spacing(2),
    marginTop: 10,
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(4),
    },
    backgroundImage:
        'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
        backgroundImage:
            'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
    }),
}));

function RegistrationPage() {

    const [awaitingEmailVerification, setAwaitingEmailVerification] = useState(false);
    const [nameError, setNameError] = React.useState(false);
    const [nameErrorMessage, setNameErrorMessage] = React.useState('');
    const [emailError, setEmailError] = React.useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
    const [passwordError, setPasswordError] = React.useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
    const [isRegistering, setIsRegistering] = useState(false)

    const { userLoggedIn } = useAuth()

    const navigate = useNavigate();

    useEffect(() => {
        if (awaitingEmailVerification) {
            doSignOut()
        }
    }, [awaitingEmailVerification]);


    const validateInputs = () => {
        const email = document.getElementById('email') as HTMLInputElement;
        const password = document.getElementById('password') as HTMLInputElement;
        const name = document.getElementById('name') as HTMLInputElement;

        let isValid = true;

        if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
            setEmailError(true);
            setEmailErrorMessage('Please enter a valid email address.');
            isValid = false;
        } else {
            setEmailError(false);
            setEmailErrorMessage('');
        }

        if (!password.value || password.value.length < 6) {
            setPasswordError(true);
            setPasswordErrorMessage('Password must be at least 6 characters long.');
            isValid = false;
        } else {
            setPasswordError(false);
            setPasswordErrorMessage('');
        }

        if (!name.value || name.value.length < 1) {
            setNameError(true);
            setNameErrorMessage('Name is required.');
            isValid = false;
        } else {
            setNameError(false);
            setNameErrorMessage('');
        }

        return isValid;
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        if (!isRegistering) {
            setIsRegistering(true)
            doCreateUserWithEmailAndPassword(
                data.get('email'),
                data.get('password'))
                .then(async () => {
                    let firebaseUser = auth.currentUser;
                    if (firebaseUser) {
                        updateProfile(firebaseUser, {
                            displayName: data.get('name') as string
                        })

                        window.fetch('https://www.scitility.com/account/api/v1/initialize', {
                            method: 'POST',
                            headers: {
                                'Accept': 'application/json',
                                'Content-Type': 'application/json',
                                'Authorization': 'Bearer ' + await firebaseUser.getIdToken(true)
                            },

                            //make sure to serialize your JSON body
                            body: JSON.stringify({
                                displayName: data.get('name') as string,
                                subscribeToEmailList: (data.get('allowExtraEmails') !== null)
                            })
                        })

                        doSendEmailVerification()
                            .then(() => {
                                setAwaitingEmailVerification(true)
                                setIsRegistering(false)
                            }
                            )
                    }
                })
        }
    };
    return (
        <>
            {awaitingEmailVerification &&
                <>
                    <Box sx={{ flex: '1 1', overflow: 'auto' }}>
                        <SignUpContainer direction="column" justifyContent="space-between">
                            <Card variant="outlined">
                                <Typography
                                    component="h1"
                                    variant="h4"
                                    sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
                                >
                                    Please validate your email address
                                </Typography>

                                <Typography>
                                    To access your account, please check your inbox for a verification email. Click the link in that email to confirm your address.

                                    If you don't see the email, please check your spam or junk folder. Thank you!
                                </Typography>
                            </Card>
                        </SignUpContainer>
                    </Box>
                </>
            }
            {!isRegistering && !awaitingEmailVerification && userLoggedIn && (<Navigate to={'/argos'} replace={true} />)}
            {!awaitingEmailVerification &&
                <Box sx={{ flex: '1 1', overflow: 'auto' }}>
                    <SignUpContainer direction="column" justifyContent="space-between">
                        <Card variant="outlined">
                            <Typography
                                component="h1"
                                variant="h4"
                                sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
                            >
                                Sign up
                            </Typography>
                            <Box
                                component="form"
                                onSubmit={handleSubmit}
                                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                            >
                                <FormControl>
                                    <FormLabel htmlFor="name">Full name</FormLabel>
                                    <TextField
                                        autoComplete="name"
                                        name="name"
                                        required
                                        fullWidth
                                        id="name"
                                        placeholder="Your full name"
                                        error={nameError}
                                        helperText={nameErrorMessage}
                                        color={nameError ? 'error' : 'primary'}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel htmlFor="email">Email</FormLabel>
                                    <TextField
                                        required
                                        fullWidth
                                        id="email"
                                        placeholder="your@email.com"
                                        name="email"
                                        autoComplete="email"
                                        variant="outlined"
                                        error={emailError}
                                        helperText={emailErrorMessage}
                                        color={passwordError ? 'error' : 'primary'}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel htmlFor="password">Password</FormLabel>
                                    <TextField
                                        required
                                        fullWidth
                                        name="password"
                                        placeholder="••••••"
                                        type="password"
                                        id="password"
                                        autoComplete="new-password"
                                        variant="outlined"
                                        error={passwordError}
                                        helperText={passwordErrorMessage}
                                        color={passwordError ? 'error' : 'primary'}
                                    />
                                </FormControl>
                                <FormControlLabel
                                    control={<Checkbox value="allowExtraEmails" name="allowExtraEmails" id="allowExtraEmails" color="primary" />}
                                    label="I want to receive updates via email."
                                />
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    onClick={validateInputs}
                                >
                                    Sign up
                                </Button>
                                <Typography sx={{ textAlign: 'center' }}>
                                    Already have an account?{' '}
                                    <span>
                                        <Link
                                            href="/argos"
                                            variant="body2"
                                            sx={{ alignSelf: 'center' }}
                                        >
                                            Sign in
                                        </Link>
                                    </span>
                                </Typography>
                            </Box>
                            <Divider>
                                <Typography sx={{ color: 'text.secondary' }}>or</Typography>
                            </Divider>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={doSignInWithOrcid}
                                    startIcon={
                                        <Avatar src={'/argos/static/images/ORCID_iD.svg'} />
                                    }
                                >
                                    Sign up with Orcid
                                </Button>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={doSignInWithGoogle}
                                    startIcon={<GoogleIcon />}
                                >
                                    Sign up with Google
                                </Button>

                            </Box>
                        </Card>
                    </SignUpContainer>
                </Box>}
        </>

    )
}

export default RegistrationPage;